import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import TimezoneSelect from "@/components/TimezoneSelect";
import { toast } from "sonner";

export default function PubProfile() {
  const { user, checkAuth } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPw, setChangingPw] = useState(false);
  const [pwError, setPwError] = useState("");
  // Notification preferences — currently just the rate-drift digest
  // toggle; keep state lean so adding future flags is one line each.
  const [driftOptOut, setDriftOptOut] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const savedTimezone = user?.timezone || "UTC";
  const [draftTimezone, setDraftTimezone] = useState(savedTimezone);
  const [savingTz, setSavingTz] = useState(false);

  useEffect(() => {
    setDraftTimezone(savedTimezone);
  }, [savedTimezone]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await API.get("/publisher/notification-preferences");
        setDriftOptOut(!!data?.rate_drift_digest_optout);
      } catch (err) { /* prefs are non-blocking */ }
    })();
  }, []);

  const handleToggleDrift = async (next) => {
    setSavingPrefs(true);
    setDriftOptOut(next);  // optimistic
    try {
      await API.post("/publisher/notification-preferences", { rate_drift_digest_optout: next });
      toast.success(
        next
          ? "You'll stop receiving rate-drift digest emails."
          : "You'll receive rate-drift digest emails when your brands move.",
      );
    } catch (err) {
      setDriftOptOut(!next);  // revert
      toast.error(err.response?.data?.detail || "Failed to update preference");
    } finally {
      setSavingPrefs(false);
    }
  };

  const timezoneDirty = draftTimezone !== savedTimezone;

  const handleApplyTimezone = async () => {
    if (!timezoneDirty || !draftTimezone) return;
    setSavingTz(true);
    try {
      const { data } = await API.put("/publisher/timezone", { timezone: draftTimezone });
      const saved = data?.timezone || draftTimezone;
      setDraftTimezone(saved);
      toast.success(`Timezone updated to ${saved}`);
      if (typeof checkAuth === "function") await checkAuth();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to update timezone");
    } finally {
      setSavingTz(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwError("");
    if (newPassword !== confirmPassword) { setPwError("New passwords do not match"); return; }
    if (newPassword.length < 6) { setPwError("Password must be at least 6 characters"); return; }
    setChangingPw(true);
    try {
      await API.post("/auth/change-password", { current_password: currentPassword, new_password: newPassword });
      toast.success("Password changed successfully");
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
    } catch (err) {
      setPwError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setChangingPw(false);
    }
  };

  return (
    <div className="p-8" data-testid="publisher-profile-page">
      <div className="mb-6">
        <p className="text-[11px] font-bold tracking-[0.3em] uppercase text-slate-400 mb-2">
          Account / Profile
        </p>
        <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight leading-none">Profile</h1>
        <p className="text-sm text-slate-500 mt-2">Your account information and security</p>
      </div>

      <div className="max-w-lg space-y-6">
        {/* Account Info */}
        <div className="border border-slate-200 rounded-md p-6 space-y-4">
          <h3 className="font-heading text-sm font-semibold text-slate-900 mb-2">Account Details</h3>
          {[
            { label: "Company Name", value: user?.company_name },
            { label: "Name", value: `${user?.first_name || ""} ${user?.last_name || ""}` },
            { label: "Email", value: user?.email },
            { label: "subId1", value: user?.sub_id, mono: true },
            { label: "Status", value: user?.status || "active" },
          ].map((item) => (
            <div key={item.label} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
              <span className="text-xs uppercase tracking-wider text-slate-500 font-bold">{item.label}</span>
              <span className={`text-sm text-slate-900 ${item.mono ? "font-mono font-bold text-[#002FA7]" : "font-medium"}`}>{item.value || "-"}</span>
            </div>
          ))}
        </div>

        {/* Timezone */}
        <div className="border border-slate-200 rounded-md p-6" data-testid="timezone-prefs-card">
          <h3 className="font-heading text-sm font-semibold text-slate-900 mb-2">Timezone</h3>
          <p className="text-xs text-slate-500 mb-4 leading-relaxed">
            Used for clicks-log date ranges, timestamps, and daily click-cap reset.
            Click data is still stored in UTC. Select a timezone, then click Apply.
          </p>
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0">
              <TimezoneSelect
                value={draftTimezone}
                onChange={setDraftTimezone}
                disabled={savingTz}
                listEndpoint="/publisher/timezones"
                testId="pub-timezone-select"
              />
            </div>
            <Button
              type="button"
              className="h-10 px-4 shrink-0 bg-[#002FA7] hover:bg-[#00227A] text-white"
              disabled={!timezoneDirty || savingTz}
              onClick={handleApplyTimezone}
              data-testid="pub-timezone-apply"
            >
              {savingTz ? "Saving…" : "Apply"}
            </Button>
          </div>
        </div>

        {/* Email notifications */}
        <div className="border border-slate-200 rounded-md p-6" data-testid="notification-prefs-card">
          <h3 className="font-heading text-sm font-semibold text-slate-900 mb-4">Email notifications</h3>
          <div className="flex items-start justify-between gap-4 py-3 border-b border-slate-100 last:border-0">
            <div className="flex-1">
              <div className="text-sm font-semibold text-slate-900">Rate-drift digest</div>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                Once a day we send you a summary of any brand <b>you&apos;re running</b> whose upstream rate
                moved ≥ ±0.25%, got disabled, or got re-enabled. Stops you from
                discovering rate cuts the hard way.
              </p>
            </div>
            <Switch
              checked={!driftOptOut}
              disabled={savingPrefs}
              onCheckedChange={(checked) => handleToggleDrift(!checked)}
              data-testid="rate-drift-digest-toggle"
            />
          </div>
        </div>

        {/* Change Password */}
        <div className="border border-slate-200 rounded-md p-6">
          <h3 className="font-heading text-sm font-semibold text-slate-900 mb-4">Change Password</h3>
          <form onSubmit={handleChangePassword} className="space-y-4">
            {pwError && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="change-pw-error">{pwError}</div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-500 font-bold">Current Password</Label>
              <Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required className="h-10" data-testid="current-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-500 font-bold">New Password</Label>
              <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required className="h-10" data-testid="new-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-500 font-bold">Confirm New Password</Label>
              <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required className="h-10" data-testid="confirm-new-password-input" />
            </div>
            <Button type="submit" disabled={changingPw} className="bg-[#002FA7] hover:bg-[#00227A] text-white" data-testid="change-password-btn">
              {changingPw ? "Changing..." : "Change Password"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
